// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React from "react";
import PropTypes from "prop-types";

import {
  alpha,
  Box,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Typography,
  toggleButtonClasses,
  typographyClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { noop } from "../util";

const StyledToggleButton = styled(MuiToggleButton)(
  ({ theme: { palette, shape, spacing } }) => ({
    [`&.${toggleButtonClasses.root}`]: {
      border: `1px solid ${palette.divider}`,
      borderRadius: shape.borderRadius,
      boxSizing: "border-box",
      height: "134px",
      width: "100%",

      "&:not(:first-of-type)": {
        border: `1px solid ${palette.divider}`,
        borderRadius: shape.borderRadius,

        marginLeft: spacing(1),
      },

      "&:not(:last-child)": {
        borderRadius: shape.borderRadius,
        marginRight: spacing(1),
      },

      "&:hover": {
        backgroundColor: (props) =>
          alpha(palette[props.color].main, palette.action.hoverOpacity),
      },
    },

    [`&.${toggleButtonClasses.selected}`]: {
      backgroundColor: (props) =>
        alpha(palette[props.color].main, palette.action.selectedOpacity),
      border: (props) => `1px solid ${palette[props.color].main}`,
      color: (props) => palette[props.color].main,

      "&:hover": {
        backgroundColor: (props) =>
          alpha(palette[props.color].main, palette.action.hoverOpacity),
        color: (props) => palette[props.color].main,
      },
    },
  })
);

const StyledTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: {
    textTransform: "none",
  },
}));

function MultipleToggleButtonGroup({ value, onChange, buttons, name, color }) {
  const handleChange = (event, val) => {
    event.target.name = name;
    event.target.value = val;
    onChange(event, val);
  };

  return (
    <Box width="100%">
      <MuiToggleButtonGroup value={value} exclusive onChange={handleChange}>
        {buttons.map((button) => (
          <StyledToggleButton
            value={button.value}
            key={button.value}
            color={color}
          >
            <Box display="flex" flexDirection="column" alignItems="center">
              {button.icon ?? <>{button.icon}</>}
              <StyledTypography variant="body1">
                {button.label}
              </StyledTypography>
            </Box>
          </StyledToggleButton>
        ))}
      </MuiToggleButtonGroup>
    </Box>
  );
}

MultipleToggleButtonGroup.defaultProps = {
  onChange: noop,
  color: "primary",
};

MultipleToggleButtonGroup.propTypes = {
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      icon: PropTypes.element,
    })
  ).isRequired,
  color: PropTypes.oneOf([
    "error",
    "info",
    "primary",
    "secondary",
    "success",
    "warning",
  ]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default MultipleToggleButtonGroup;
